
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { supabase } from '@/integrations/supabase/client';
import { CalendarEvent } from '../types';

interface GuardShiftData {
  guardId: string;
  guardName: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  afm: string;
  code: string;
  licenseExpiryDate: string;
  hiringDate: string;
  dateOfBirth: string;
  shiftTitle: string;
  shiftDate: string;
  shiftStart: string;
  shiftEnd: string;
  shiftHours: number;
  shiftMinutes: number;
  shiftDurationDecimal: number;
  shiftDurationHours: number;
  shiftDurationMinutes: number;
  teamName: string;
}

export const exportGuardShiftsToExcel = async (
  teamId: string,
  dateRange?: { start: Date; end: Date }
) => {
  try {
    console.log("Starting export for team:", teamId);
    
    // Fetch team info
    const { data: teamData } = await supabase
      .from('teams')
      .select('name')
      .eq('id', teamId)
      .single();
    
    const teamName = teamData?.name || 'Unknown Team';
    
    // 1. Fetch all schedules for the team
    let schedulesQuery = supabase
      .from('team_schedules')
      .select('*')
      .eq('team_id', teamId);
    
    // Apply date filtering if provided
    if (dateRange) {
      const startDateStr = dateRange.start.toISOString();
      const endDateStr = dateRange.end.toISOString();
      schedulesQuery = schedulesQuery
        .gte('start_date', startDateStr)
        .lte('end_date', endDateStr);
    }
    
    const { data: schedules, error: schedulesError } = await schedulesQuery;
    
    if (schedulesError) throw schedulesError;
    if (!schedules || schedules.length === 0) {
      throw new Error("No schedules found for this team");
    }
    
    // 2. Collect all guard IDs from schedules
    const guardIds = new Set<string>();
    schedules.forEach(schedule => {
      if (schedule.assigned_guards && schedule.assigned_guards.length > 0) {
        schedule.assigned_guards.forEach(guardId => guardIds.add(guardId));
      }
    });
    
    if (guardIds.size === 0) {
      throw new Error("No guards assigned to schedules");
    }
    
    // 3. Fetch guard profile information (including archived guards for billing)
    const { data: guardsData, error: guardsError } = await supabase
      .from('profiles')
      .select('*')
      .in('id', Array.from(guardIds));
    
    if (guardsError) throw guardsError;
    if (!guardsData || guardsData.length === 0) {
      throw new Error("Could not retrieve guard profiles");
    }
    
    console.log(`Found ${guardsData.length} guards and ${schedules.length} schedules`);
    
    // 4. Process data to combine guard info with shifts
    const exportData: GuardShiftData[] = [];
    
    for (const schedule of schedules) {
      const startDate = fromLocalISOString(schedule.start_date);
      const endDate = fromLocalISOString(schedule.end_date);
      const durationMinutes = (endDate.getTime() - startDate.getTime()) / (1000 * 60);
      const shiftHours = Math.floor(durationMinutes / 60);
      const shiftMinutes = Math.round(durationMinutes % 60);
      const shiftDurationDecimal = durationMinutes / 60;
      const durationHoursWhole = Math.floor(durationMinutes / 60);
      const durationMinutesRemainder = Math.round(durationMinutes % 60);
      
      // Format dates for display
      const shiftDate = format(startDate, 'dd/MM/yyyy');
      const shiftStart = format(startDate, 'HH:mm');
      const shiftEnd = format(endDate, 'HH:mm');
      
      // Process assigned guards
      if (schedule.assigned_guards && schedule.assigned_guards.length > 0) {
        for (const guardId of schedule.assigned_guards) {
          const guard = guardsData.find(g => g.id === guardId);
          
          if (guard) {
            exportData.push({
              guardId: guard.id,
              guardName: `${guard.full_name || `${guard.first_name || ''} ${guard.last_name || ''}`.trim()}${guard.status === 'archived' ? ' (ARCHIVED)' : ''}`,
              firstName: guard.first_name || '',
              lastName: guard.last_name || '',
              email: guard.email || '',
              mobile: guard.mobile_phone || '',
              afm: guard.afm || '',
              code: guard.code || '',
              licenseExpiryDate: guard.license_expiry_date ? format(new Date(guard.license_expiry_date), 'dd/MM/yyyy') : '',
              hiringDate: guard.hiring_date ? format(new Date(guard.hiring_date), 'dd/MM/yyyy') : '',
              dateOfBirth: guard.date_of_birth ? format(new Date(guard.date_of_birth), 'dd/MM/yyyy') : '',
              shiftTitle: schedule.title,
              shiftDate: shiftDate,
              shiftStart: shiftStart,
              shiftEnd: shiftEnd,
              shiftHours: shiftHours,
              shiftMinutes: shiftMinutes,
              shiftDurationDecimal: Math.round(shiftDurationDecimal * 100) / 100,
              shiftDurationHours: durationHoursWhole,
              shiftDurationMinutes: durationMinutesRemainder,
              teamName: teamName
            });
          }
        }
      }
    }
    
    // 5. Create Excel workbook
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Apply formatting to distinguish the new hours/minutes columns
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let row = 1; row <= range.e.r; row++) {
      // Format decimal hours column (normal)
      const decimalCell = ws[XLSX.utils.encode_cell({r: row, c: 19})]; // shiftDurationDecimal column
      if (decimalCell) {
        decimalCell.z = '0.00'; // Two decimal places
      }
      
      // Format hours column (bold)
      const hoursCell = ws[XLSX.utils.encode_cell({r: row, c: 20})]; // shiftDurationHours column
      if (hoursCell) {
        hoursCell.s = { font: { bold: true }, fill: { fgColor: { rgb: "E8F4FD" } } };
      }
      
      // Format minutes column (bold)
      const minutesCell = ws[XLSX.utils.encode_cell({r: row, c: 21})]; // shiftDurationMinutes column
      if (minutesCell) {
        minutesCell.s = { font: { bold: true }, fill: { fgColor: { rgb: "E8F4FD" } } };
      }
    }
    
    // Set column widths
    const colWidths = [
      { wch: 36 }, // guardId
      { wch: 20 }, // guardName
      { wch: 15 }, // firstName
      { wch: 15 }, // lastName
      { wch: 25 }, // email
      { wch: 15 }, // mobile
      { wch: 12 }, // afm
      { wch: 12 }, // code
      { wch: 15 }, // licenseExpiryDate
      { wch: 15 }, // hiringDate
      { wch: 15 }, // dateOfBirth
      { wch: 20 }, // shiftTitle
      { wch: 12 }, // shiftDate
      { wch: 10 }, // shiftStart
      { wch: 10 }, // shiftEnd
      { wch: 8 },  // shiftHours
      { wch: 10 }, // shiftMinutes
      { wch: 12 }, // shiftDurationDecimal
      { wch: 8 },  // shiftDurationHours (Ώρες)
      { wch: 10 }, // shiftDurationMinutes (Λεπτά)
      { wch: 20 }  // teamName
    ];
    ws['!cols'] = colWidths;
    
    // Create workbook and download
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Guard Schedules");
    
    // Get date range string for filename
    let dateRangeStr = '';
    if (dateRange) {
      dateRangeStr = `_${format(dateRange.start, 'yyyyMMdd')}_${format(dateRange.end, 'yyyyMMdd')}`;
    }
    
    // Generate filename
    const fileName = `${teamName.replace(/\s+/g, '_')}_guard_schedules${dateRangeStr}.xlsx`;
    
    // Download file
    XLSX.writeFile(wb, fileName);
    
    return {
      success: true,
      fileName,
      recordCount: exportData.length
    };
  } catch (error) {
    console.error("Error exporting guard shifts:", error);
    throw error;
  }
};
